import { Link } from "react-router-dom";
import {
  ArrowRight,
  Sparkles,
  Layers,
  Trophy,
  BarChart3,
  Building,
  School,
  Store,
  Briefcase,
  Compass,
  Search,
  AlertTriangle,
  Target,
  ClipboardCheck,
  Cpu,
  ShieldCheck,
  CheckCircle2,
} from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import Header from "@/components/politics/Header";
import Footer from "@/components/politics/Footer";
import SaasLeadForm from "@/components/politics/SaasLeadForm";

/* ============================================================= */
/*  1 — Hero                                                     */
/* ============================================================= */
function SaasHero() {
  return (
    <section
      data-testid="saas-hero"
      className="relative isolate overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28"
    >
      <div className="absolute inset-0 -z-10 rsm-grid-bg opacity-90" />
      <div
        className="absolute -top-32 right-[-12%] -z-10 h-[520px] w-[520px] rounded-full blur-3xl opacity-30"
        style={{
          background: "radial-gradient(closest-side, #3ED383, transparent 70%)",
        }}
      />
      <div
        className="absolute -bottom-40 left-[-12%] -z-10 h-[460px] w-[460px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #208BFF, transparent 70%)",
        }}
      />

      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
        <div className="lg:col-span-8 rsm-rise">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#1B1D1D]/10 bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold tracking-[0.18em] uppercase text-[#1B1D1D]">
            <Sparkles size={12} className="text-[#3ED383]" />
            RSM Rede Dedicada · SaaS para organizações
          </div>
          <h1
            data-testid="saas-headline"
            className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.94] text-[#1B1D1D]"
          >
            Transforme sua comunidade em uma{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-[#208BFF]">rede engajada</span>
              <span className="absolute inset-x-0 bottom-1 h-3 -z-0 bg-[#208BFF]/15 rounded" />
            </span>{" "}
            de missões e resultados.
          </h1>
          <p
            data-testid="saas-subhead"
            className="mt-8 max-w-3xl text-lg sm:text-xl leading-relaxed text-[#51565F]"
          >
            A <span className="font-medium text-[#1B1D1D]">RSM Rede Dedicada</span>{" "}
            é a versão SaaS da nossa plataforma de base, aplicada a escolas,
            entidades de classe e varejistas de grande porte.
          </p>
          <div className="mt-10 flex flex-col sm:flex-row gap-3">
            <a
              href="#diagnostico"
              data-testid="saas-hero-cta"
              className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#208BFF] px-7 py-4 text-base font-semibold text-white shadow-sm transition-all duration-300 ease-out hover:bg-[#1B73D6] hover:-translate-y-0.5 focus:outline-none focus:ring-2 focus:ring-[#208BFF] focus:ring-offset-2 focus:ring-offset-[#F5FAFB]"
            >
              Quero avaliar a RSM na minha organização
              <ArrowRight
                size={18}
                className="transition-transform group-hover:translate-x-1"
              />
            </a>
            <Link
              to="/politics"
              data-testid="saas-hero-secondary"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1B1D1D]/15 bg-white/70 backdrop-blur px-7 py-4 text-base font-medium text-[#1B1D1D] hover:border-[#3ED383] hover:text-[#1B73D6] transition-all duration-300 ease-out"
            >
              Conhecer RSM Politics
            </Link>
          </div>
          <p
            data-testid="saas-microcopy"
            className="mt-6 text-sm text-[#7D8188] max-w-xl"
          >
            Vamos entender sua realidade e, se fizer sentido, desenhar um piloto
            juntos.
          </p>
        </div>

        {/* Side: micro dashboard */}
        <div className="lg:col-span-4 lg:pl-4 rsm-rise rsm-rise-delay-2">
          <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <span className="text-xs font-semibold tracking-[0.18em] uppercase text-[#7D8188]">
                Painel · Comunidade
              </span>
              <span className="inline-flex items-center gap-1 rounded-full bg-[#3ED383]/12 px-2 py-0.5 text-[10px] font-semibold text-[#1d8b51]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#3ED383] animate-pulse" />
                AO VIVO
              </span>
            </div>
            <div className="mt-5 space-y-4">
              <Stat label="Participantes ativos" value="3.842" delta="+9,1%" />
              <Stat label="Missões cumpridas" value="1.207" delta="+22%" />
              <Stat label="NPS interno" value="71" delta="↑ +4 pts" positive />
            </div>
            <div className="mt-6 border-t border-[#1B1D1D]/8 pt-4">
              <span className="text-xs font-semibold uppercase tracking-wider text-[#7D8188]">
                Engajamento por unidade
              </span>
              <div className="mt-3 flex items-end gap-1.5 h-16">
                {[48, 70, 55, 82, 60, 88, 74, 68, 76, 90, 65, 80].map((v, i) => (
                  <div
                    key={`bar-${v}`}
                    className="flex-1 rounded-sm bg-gradient-to-t from-[#3ED383]/15 to-[#3ED383]"
                    style={{ height: `${v}%` }}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Stat({ label, value, delta, positive = true }) {
  return (
    <div className="flex items-end justify-between">
      <div>
        <div className="text-xs uppercase tracking-wider text-[#7D8188] font-semibold">
          {label}
        </div>
        <div className="font-display text-2xl font-bold tracking-tight text-[#1B1D1D] mt-1">
          {value}
        </div>
      </div>
      <span
        className={`text-xs font-semibold ${
          positive ? "text-[#1d8b51]" : "text-[#D94A0A]"
        }`}
      >
        {delta}
      </span>
    </div>
  );
}

/* ============================================================= */
/*  2 — Why (SPIN narrative)                                     */
/* ============================================================= */
function WhySection() {
  const blocks = [
    {
      icon: Search,
      eyebrow: "Situação",
      title: "Sua comunidade está cercada de canais — mas escuta de verdade?",
      body: "Alunos, pais, associados, colaboradores recebem dezenas de mensagens por dia, vindas de WhatsApp, e-mail, intranet e redes. A comunicação flui em uma direção só: da organização para o público.",
      accent: "#208BFF",
    },
    {
      icon: AlertTriangle,
      eyebrow: "Problema",
      title: "Você fala, mas pouca coisa volta — de forma estruturada.",
      body: "Não dá para saber, em tempo real, quem participa de fato, quem está insatisfeito, ou onde nasce uma nova demanda. Engajamento vira sensação, não dado.",
      accent: "#FA9F22",
    },
    {
      icon: Target,
      eyebrow: "Implicação",
      title: "Decisões importantes são tomadas no escuro.",
      body: "Comunicação, programas e benefícios são desenhados na base do achismo. A sensação de distância cresce. Oportunidades de mobilização — e de identificar problemas cedo — passam batidas.",
      accent: "#D94A0A",
    },
  ];

  return (
    <section
      id="por-que"
      data-testid="saas-why"
      className="relative py-24 sm:py-32 border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Por que repensar engajamento
          </span>
          <h2
            data-testid="saas-why-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Comunidades fortes exigem mais que comunicação de mão única.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl">
            Antes de falar de plataforma, vale parar para olhar de onde a maioria
            das organizações está partindo hoje — e por que isso já não é
            suficiente.
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4">
          {blocks.map((b, i) => {
            const Icon = b.icon;
            return (
              <article
                key={b.eyebrow}
                data-testid={`saas-why-card-${i}`}
                className="group relative flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md"
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${b.accent}15`,
                    color: b.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <span
                  className="mt-5 text-[10px] font-bold tracking-[0.22em] uppercase"
                  style={{ color: b.accent }}
                >
                  {b.eyebrow}
                </span>
                <h3 className="mt-2 font-display text-xl font-semibold leading-tight text-[#1B1D1D]">
                  {b.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {b.body}
                </p>
              </article>
            );
          })}
        </div>

        <div className="mt-12 rounded-2xl border border-[#208BFF]/20 bg-gradient-to-br from-white to-[#208BFF]/5 p-8 sm:p-10">
          <p
            data-testid="saas-why-closing"
            className="font-display text-2xl sm:text-3xl tracking-tight text-[#1B1D1D] leading-tight max-w-4xl"
          >
            A RSM acredita que comunidades fortes exigem{" "}
            <span className="text-[#208BFF]">canais ativos de participação</span>
            , engajamento e inteligência — não só comunicação de mão única.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  3 — How                                                       */
/* ============================================================= */
const HOW_PILLARS = [
  {
    icon: Building,
    title: "Ambiente próprio da sua organização",
    desc: "Um espaço com a sua marca, onde a comunidade é convidada a participar. Sem ruído de feed alheio — só o que pertence à sua operação.",
    accent: "#208BFF",
  },
  {
    icon: Layers,
    title: "Missões digitais e presenciais",
    desc: "Responder pesquisas, divulgar eventos, cumprir tarefas ligadas a metas internas, participar de campanhas — tudo desenhado com a sua linguagem.",
    accent: "#7E20FF",
  },
  {
    icon: Trophy,
    title: "Gamificação e incentivos",
    desc: "Pontos, recompensas e reconhecimento mantêm a participação contínua. Você decide a moeda e as regras.",
    accent: "#FA9F22",
  },
  {
    icon: BarChart3,
    title: "Dados e relatórios em tempo real",
    desc: "Quem engaja, quais ações funcionam, onde estão os gargalos. Engajamento deixa de ser sensação e vira indicador.",
    accent: "#3ED383",
  },
];

function HowSection() {
  return (
    <section
      id="como-funciona"
      data-testid="saas-how"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Como funciona uma Rede Dedicada
          </span>
          <h2
            data-testid="saas-how-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Quatro engrenagens que viram uma comunidade ativa.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {HOW_PILLARS.map((pillar, i) => {
            const Icon = pillar.icon;
            return (
              <article
                key={pillar.title}
                data-testid={`saas-how-card-${i}`}
                className="group relative overflow-hidden rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div
                  className="grid h-11 w-11 place-items-center rounded-xl"
                  style={{
                    backgroundColor: `${pillar.accent}15`,
                    color: pillar.accent,
                  }}
                >
                  <Icon size={22} />
                </div>
                <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
                  {pillar.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {pillar.desc}
                </p>
                <span
                  className="absolute -bottom-12 -right-12 h-32 w-32 rounded-full opacity-0 transition-opacity duration-500 group-hover:opacity-15"
                  style={{ background: pillar.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  4 — Personas / examples                                      */
/* ============================================================= */
const PERSONAS = [
  {
    icon: School,
    title: "Escolas",
    role: "Educacional",
    desc: "Engajar alunos, pais e professores em projetos, campanhas de matrícula e ações comunitárias.",
    missions: [
      "Divulgação de eventos pela rede de pais e alunos.",
      "Pesquisas de satisfação contínuas por turma e turno.",
      "Desafios pedagógicos com pontos e recompensas.",
      "Registro de presença em ações e atividades extracurriculares.",
    ],
    accent: "#208BFF",
  },
  {
    icon: Briefcase,
    title: "Entidades de classe",
    role: "Associativo",
    desc: "Mobilizar associados em assembleias, pautas relevantes e campanhas de valorização da categoria.",
    missions: [
      "Enquetes rápidas sobre pautas e demandas.",
      "Mobilização para assembleias e eventos.",
      "Compartilhamento de conteúdo institucional pela rede.",
      "Mapeamento de regiões com baixa participação.",
    ],
    accent: "#7E20FF",
  },
  {
    icon: Store,
    title: "Varejistas de grande porte",
    role: "Varejo & franquias",
    desc: "Engajar colaboradores, promotores, franqueados e clientes VIP em metas, lançamentos e campanhas.",
    missions: [
      "Ativações de campanha em loja por colaboradores.",
      "Coleta de feedback de clientes e mystery shoppers internos.",
      "Divulgação de promoções pela rede dos próprios times.",
      "Pesquisas de clima por unidade e regional.",
    ],
    accent: "#3ED383",
  },
];

function PersonasSection() {
  return (
    <section
      id="aplicacoes"
      data-testid="saas-personas"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Onde a Rede Dedicada se aplica
          </span>
          <h2
            data-testid="saas-personas-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Três tipos de organização. A mesma engine.
          </h2>
        </div>

        <div className="mt-12 grid grid-cols-1 lg:grid-cols-3 gap-4">
          {PERSONAS.map((p, i) => {
            const Icon = p.icon;
            return (
              <article
                key={p.title}
                data-testid={`saas-persona-${i}`}
                className="group relative flex flex-col rounded-2xl border border-[#1B1D1D]/8 bg-white p-7 transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-md hover:border-[#1B1D1D]/15"
              >
                <div className="flex items-center justify-between">
                  <div
                    className="grid h-11 w-11 place-items-center rounded-xl"
                    style={{
                      backgroundColor: `${p.accent}15`,
                      color: p.accent,
                    }}
                  >
                    <Icon size={22} />
                  </div>
                  <span
                    className="text-[10px] font-bold tracking-[0.22em] uppercase"
                    style={{ color: p.accent }}
                  >
                    {p.role}
                  </span>
                </div>
                <h3 className="mt-5 font-display text-2xl font-semibold text-[#1B1D1D]">
                  {p.title}
                </h3>
                <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
                  {p.desc}
                </p>
                <span className="mt-6 text-[11px] font-semibold tracking-[0.18em] uppercase text-[#7D8188]">
                  Exemplos de missões
                </span>
                <ul className="mt-3 space-y-2.5 flex-1">
                  {p.missions.map((m, j) => (
                    <li
                      key={j}
                      className="flex items-start gap-2.5 text-sm leading-relaxed text-[#51565F]"
                    >
                      <CheckCircle2
                        size={15}
                        className="mt-0.5 flex-shrink-0"
                        style={{ color: p.accent }}
                      />
                      <span>{m}</span>
                    </li>
                  ))}
                </ul>
                <div
                  className="mt-6 h-1 w-10 rounded-full transition-all duration-300 group-hover:w-20"
                  style={{ background: p.accent }}
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  5 — Product status                                           */
/* ============================================================= */
function StatusSection() {
  return (
    <section
      data-testid="saas-status"
      className="relative py-24 sm:py-32 bg-[#1B1D1D] text-[#F5FAFB] overflow-hidden border-y border-[#1B1D1D]/8"
    >
      <div
        className="absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            "radial-gradient(circle at 1px 1px, #F5FAFB 1px, transparent 0)",
          backgroundSize: "32px 32px",
        }}
      />
      <div
        className="absolute -top-32 right-[-10%] h-[420px] w-[420px] rounded-full blur-3xl opacity-25"
        style={{
          background: "radial-gradient(closest-side, #3ED383, transparent 70%)",
        }}
      />

      <div className="relative mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.22em] uppercase text-[#3ED383]">
            <Compass size={14} /> Onde o produto está hoje
          </span>
          <h2
            data-testid="saas-status-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[0.98] text-[#F5FAFB]"
          >
            Fase de pilotos e cases — não de prateleira.
          </h2>
          <p className="mt-6 max-w-2xl text-base sm:text-lg leading-relaxed text-[#F5FAFB]/75">
            A RSM Rede Dedicada está em seleção de projetos piloto. Buscamos
            organizações com desafios interessantes de engajamento para construir{" "}
            <span className="text-[#F5FAFB] font-medium">cases de alto impacto</span>
            . A contratação é consultiva — e, no futuro, vai amadurecer para um
            modelo mais auto-serviço.
          </p>
        </div>
        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur p-7">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#3ED383]/15 text-[#3ED383]">
              <ShieldCheck size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#F5FAFB]">
              O que isso significa pra você
            </h3>
            <ul className="mt-4 space-y-3 text-sm text-[#F5FAFB]/75">
              {[
                "Atenção dedicada do time da RSM no piloto.",
                "Desenho sob medida — não um plano padronizado.",
                "Tarifa especial de fase de cases para os primeiros parceiros.",
              ].map((t, i) => (
                <li key={t} className="flex items-start gap-2.5">
                  <CheckCircle2
                    size={15}
                    className="mt-0.5 text-[#3ED383] flex-shrink-0"
                  />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  6 — SPIN process                                             */
/* ============================================================= */
const SPIN_STEPS = [
  {
    n: "01",
    label: "Situação",
    title: "Entendemos como sua comunidade funciona hoje",
    desc: "Canais, tamanho, dinâmica, ritmo de comunicação, ferramentas atuais.",
    color: "#208BFF",
  },
  {
    n: "02",
    label: "Problema",
    title: "Identificamos onde falta engajamento ou feedback real",
    desc: "Onde o que você fala não retorna, e onde você não enxerga o que importa.",
    color: "#FA9F22",
  },
  {
    n: "03",
    label: "Implicação",
    title: "Estimamos o impacto disso em resultados concretos",
    desc: "Retenção, participação, vendas, clima organizacional, conversão.",
    color: "#D94A0A",
  },
  {
    n: "04",
    label: "Necessidade / Piloto",
    title: "Desenhamos um piloto que faz sentido pra você",
    desc: "Escopo claro, segmento alvo, métricas combinadas — para gerar caso de uso real.",
    color: "#3ED383",
  },
];

function SpinSection() {
  return (
    <section
      id="diagnostico-processo"
      data-testid="saas-spin"
      className="relative py-24 sm:py-32 bg-white border-y border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-7xl px-6">
        <div className="max-w-3xl">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#7E20FF]">
            Processo de diagnóstico
          </span>
          <h2
            data-testid="saas-spin-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Quatro conversas estruturadas — não quatro propostas.
          </h2>
          <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#51565F]">
            Antes de qualquer contrato, fazemos um diagnóstico SPIN simples,
            humano e útil. Mesmo que a parceria não avance, você sai com um mapa
            do seu engajamento.
          </p>
        </div>

        <ol className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {SPIN_STEPS.map((s, i) => (
            <li
              key={s.n}
              data-testid={`saas-spin-step-${i}`}
              className="relative rounded-2xl border border-[#1B1D1D]/8 bg-[#F5FAFB] p-7 transition-all duration-300 ease-out hover:-translate-y-1"
            >
              <span
                className="font-mono text-sm font-semibold"
                style={{ color: s.color }}
              >
                {s.n}
              </span>
              <span
                className="block mt-2 text-[10px] font-bold tracking-[0.22em] uppercase"
                style={{ color: s.color }}
              >
                {s.label}
              </span>
              <h3 className="mt-3 font-display text-lg font-semibold leading-tight text-[#1B1D1D]">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#51565F]">
                {s.desc}
              </p>
              {i < SPIN_STEPS.length - 1 && (
                <span className="hidden lg:block absolute top-1/2 right-[-14px] -translate-y-1/2 text-[#1B1D1D]/15">
                  <ArrowRight size={20} />
                </span>
              )}
            </li>
          ))}
        </ol>

        <div className="mt-14 text-center">
          <a
            href="#diagnostico"
            data-testid="saas-spin-cta"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#208BFF] px-7 py-4 text-base font-semibold text-white transition-all duration-300 ease-out hover:bg-[#1B73D6] hover:-translate-y-0.5"
          >
            Quero passar por esse diagnóstico
            <ArrowRight size={18} />
          </a>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  7 — Who is behind                                            */
/* ============================================================= */
function TeamSection() {
  return (
    <section
      data-testid="saas-team"
      className="relative py-24 sm:py-32"
    >
      <div className="mx-auto max-w-7xl px-6 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Quem está por trás
          </span>
          <h2
            data-testid="saas-team-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            A engine que move campanhas — agora a serviço da sua organização.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#51565F] max-w-2xl">
            A tecnologia da RSM Rede Dedicada é a mesma da{" "}
            <Link
              to="/politics"
              className="text-[#208BFF] font-medium hover:underline"
            >
              RSM Politics
            </Link>{" "}
            — adaptada ao contexto corporativo e organizacional. Time com
            experiência em plataformas de engajamento, mobilização de base e
            dados.
          </p>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-xl">
            {[
              { k: "RSM Politics", v: "Engine validada" },
              { k: "Brasília", v: "Sede" },
              { k: "Consultivo", v: "Modelo atual" },
            ].map((s, i) => (
              <div
                key={s.k}
                className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-5"
              >
                <div className="font-display text-lg font-bold text-[#1B1D1D] leading-tight">
                  {s.k}
                </div>
                <div className="text-[10px] uppercase tracking-wider text-[#7D8188] mt-1 font-semibold">
                  {s.v}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5">
          <div className="rounded-2xl border border-[#1B1D1D]/8 bg-white p-8">
            <div className="grid h-12 w-12 place-items-center rounded-xl bg-[#208BFF]/12 text-[#208BFF]">
              <Cpu size={22} />
            </div>
            <h3 className="mt-5 font-display text-xl font-semibold text-[#1B1D1D]">
              Mesma plataforma. Outra aplicação.
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#51565F]">
              Em política, a engine organiza apoiadores em torno de candidatos e
              causas. Em organizações, ela organiza colaboradores, alunos ou
              associados em torno de objetivos institucionais — com a mesma
              robustez de dados e o mesmo cuidado com a base.
            </p>
            <Link
              to="/politics"
              data-testid="saas-team-cta-politics"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-[#208BFF] hover:gap-3 transition-all"
            >
              Ver como funciona em RSM Politics
              <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  8 — FAQ                                                      */
/* ============================================================= */
const FAQS = [
  {
    q: "Qual o tamanho mínimo de comunidade para fazer sentido?",
    a: "Comunidades de algumas centenas de pessoas já se beneficiam. O retorno em inteligência cresce com o volume, mas o que mais importa é a clareza do objetivo — engajamento direcionado em uma base de 500 vale mais do que ruído em uma de 50 mil.",
  },
  {
    q: "Quanto tempo leva um piloto?",
    a: "Tipicamente entre 8 e 12 semanas, do diagnóstico ao primeiro ciclo de leitura de resultados. O escopo é desenhado para gerar aprendizado real, não só uma demo.",
  },
  {
    q: "A Rede Dedicada substitui minhas ferramentas atuais (WhatsApp, e-mail, intranet)?",
    a: "Não. Ela convive — e organiza o que essas ferramentas não conseguem capturar (participação medida, missões, dados de engajamento por pessoa e por grupo). Pense como uma camada de inteligência sobre o seu ecossistema atual.",
  },
  {
    q: "Como funcionam incentivos e recompensas?",
    a: "A organização define a moeda (pontos, badges, brindes, reconhecimento público, vouchers). A plataforma cuida da mecânica — quem ganha o quê, com base em quais missões cumpridas, em qual prazo.",
  },
  {
    q: "O que é esperado da minha equipe durante o piloto?",
    a: "Uma pessoa âncora pelo lado da organização (~4 a 8 horas por semana durante o piloto), acesso à liderança para decisões rápidas e disposição para tratar engajamento como projeto estratégico — não como ferramenta isolada.",
  },
];

function SaasFAQ() {
  return (
    <section
      id="faq"
      data-testid="saas-faq"
      className="relative py-24 sm:py-32 bg-white border-t border-[#1B1D1D]/8"
    >
      <div className="mx-auto max-w-4xl px-6">
        <div className="text-center">
          <span className="text-xs font-semibold tracking-[0.22em] uppercase text-[#208BFF]">
            Perguntas frequentes
          </span>
          <h2
            data-testid="saas-faq-headline"
            className="mt-4 font-display text-4xl sm:text-5xl font-bold tracking-tighter leading-[1] text-[#1B1D1D]"
          >
            Dúvidas comuns de quem está avaliando o primeiro piloto.
          </h2>
        </div>

        <Accordion
          type="single"
          collapsible
          className="mt-12 divide-y divide-[#1B1D1D]/10 rounded-2xl border border-[#1B1D1D]/10 bg-[#F5FAFB]"
          data-testid="saas-faq-accordion"
        >
          {FAQS.map((item, i) => (
            <AccordionItem
              key={item.q}
              value={`item-${i}`}
              className="border-b-0 px-6"
              data-testid={`saas-faq-item-${i}`}
            >
              <AccordionTrigger className="text-left font-display text-lg font-semibold text-[#1B1D1D] hover:text-[#208BFF] hover:no-underline">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-[15px] leading-relaxed text-[#51565F] pb-5">
                {item.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ============================================================= */
/*  Page                                                          */
/* ============================================================= */
export default function SaasPage() {
  return (
    <main
      data-testid="saas-page"
      className="min-h-screen bg-[#F5FAFB] text-[#1B1D1D]"
    >
      <Header />
      <SaasHero />
      <WhySection />
      <HowSection />
      <PersonasSection />
      <StatusSection />
      <SpinSection />
      <TeamSection />
      <SaasFAQ />
      <SaasLeadForm />
      <Footer />
    </main>
  );
}
